import type { AuroraControlId, ClientDispatchedToolId } from 'api-validations/aurora'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import type { AssetIdentity, SatelliteImagery, SoilSurveyAttribute } from '../map'
import type { Cff } from '../operation-wide-data/browsableFields'

export type CurrentlyViewing = Cff & { layer: ViewedLayer }

export type ViewedLayer =
  | {
      kind: 'AsApplied'
      streamId: string
      applicationId: string
      layerId: string
      season: number
      crop: string
    }
  | { kind: 'Field' | 'PlanEditor' | 'BoundaryCreator' | 'PrescriptionCreator' }
  | { kind: 'Planning' | 'PlanAssetEditor'; assetType: AssetIdentity['assetType'] }
  | { kind: 'Satellite'; imagery: SatelliteImagery; captureDate: string | null }
  | { kind: 'SoilSample'; measurement: string }
  | { kind: 'SoilSurvey'; attribute: SoilSurveyAttribute }

export type AuroraControlState = {
  control: AuroraControlId
  value: string
  shown: boolean
}

/**
 * Statically-typed shape of the client-side context Aurora's agent reads.
 *
 * Each property is an independent value "slot" populated by components via
 * `useAuroraContextSlot`. Slots are optional — an absent or `undefined`
 * value drops out of the wire payload via `JSON.stringify`. The wire
 * protocol itself stays opaque (`Record<string, unknown>`); we keep this
 * shape known inside the app so adding context surfaces is type-safe and
 * discoverable.
 *
 * To add a new slot: add an optional property of type `Foo` here. No
 * other code changes are required for the merge/serialize path.
 */
export type AuroraContextSlots = {
  currentlyViewing?: CurrentlyViewing
  auroraControls?: AuroraControlState[]
}

/**
 * Aggregated state the client declares to the cloud. Two slot kinds with
 * intentionally different semantics:
 *
 *   - `contextSlots`: typed value slots flowing to SetContext. One owner
 *     per key; the owner pushes the latest value (or `undefined` to
 *     clear).
 *   - `supportedTools`: ref-counted presence flags flowing to
 *     SetSupportedTools. Multiple components can register the same id;
 *     the tool stays declared until all of them unregister.
 */
type AuroraRegistrationState = {
  contextSlots: AuroraContextSlots
  supportedTools: Partial<Record<ClientDispatchedToolId, number>>
}

export const useAuroraRegistrationStore = create<AuroraRegistrationState>()(
  subscribeWithSelector(() => ({
    contextSlots: {},
    supportedTools: {}
  }))
)

/**
 * Module-level setters held outside the store. Mirrors the map-state-store
 * convention: the store is data only, mutations live alongside. This makes
 * setter references truly stable across renders, removes selector overhead
 * for read-only hooks, and keeps the store shape easy to inspect.
 */
export const auroraRegistration = {
  /**
   * Set or clear a typed context slot. Pass `undefined` to clear.
   *
   * Deep-compares the incoming value to the slot's current value via
   * JSON.stringify and skips the setState when they match. This is what
   * lets consumers pass inline object literals safely — the slot only
   * updates when the value actually changes, regardless of reference
   * identity. The cost is one stringify per call, which is in the
   * microseconds for the payload sizes context slots carry. If a slot
   * ever needs to hold a non-serializable value (functions, Maps, class
   * instances), this assumption breaks — but such a value couldn't go on
   * the wire anyway, so the constraint already exists at the protocol
   * level; this enforces it earlier and with a clearer failure mode.
   */
  setContextSlot: <K extends keyof AuroraContextSlots>(
    key: K,
    value: AuroraContextSlots[K]
  ): void => {
    const current = useAuroraRegistrationStore.getState().contextSlots[key]
    if (JSON.stringify(current) === JSON.stringify(value)) return
    useAuroraRegistrationStore.setState((state) => ({
      contextSlots: { ...state.contextSlots, [key]: value }
    }))
  },

  /**
   * Declare support for a client-dispatched tool. Returns an unregister
   * function the caller is expected to invoke on cleanup. Internally
   * ref-counted so multiple components can register the same id without
   * stomping each other.
   */
  registerSupportedTool: (toolId: ClientDispatchedToolId): (() => void) => {
    useAuroraRegistrationStore.setState((state) => ({
      supportedTools: {
        ...state.supportedTools,
        [toolId]: (state.supportedTools[toolId] ?? 0) + 1
      }
    }))
    return () => {
      useAuroraRegistrationStore.setState((state) => {
        const current = state.supportedTools[toolId] ?? 0
        const next = { ...state.supportedTools }
        if (current <= 1) delete next[toolId]
        else next[toolId] = current - 1
        return { supportedTools: next }
      })
    }
  }
}

/** Derived view: the toolIds currently declared (count > 0), sorted. */
export const activeSupportedTools = (
  supportedTools: Partial<Record<ClientDispatchedToolId, number>>
): ClientDispatchedToolId[] => Object.keys(supportedTools).sort() as ClientDispatchedToolId[]
